
import React, { useState, useEffect } from 'react';
import { Buildings, CircleNotch, House, Warning } from '@phosphor-icons/react';
import { ladeEinheitenLeerstand } from '../../lib/dashboard/leerstand';

interface LeerstandWidgetProps {
  onNavigate?: (path: string) => void;
}

const LeerstandWidget: React.FC<LeerstandWidgetProps> = ({ onNavigate }) => {
  const [total, setTotal] = useState(0);
  const [vermietet, setVermietet] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const r = await ladeEinheitenLeerstand();
      setTotal(r.gesamt);
      setVermietet(r.belegt);
    } catch (err) {
      console.error('Error fetching leerstand:', err);
    } finally {
      setLoading(false);
    }
  };

  const leerstand = total - vermietet;
  const pct = total > 0 ? Math.round((vermietet / total) * 100) : 0;

  // SVG ring
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const filled = (pct / 100) * circumference;
  const ringFarbe = pct >= 80 ? 'var(--dw-auf-dunkel-ok)' : pct >= 50 ? 'var(--dw-auf-dunkel-warn)' : 'var(--dw-auf-dunkel-bad)';
  const leerFarbe = leerstand > 0 ? 'var(--dw-auf-dunkel-warn)' : undefined;

  // Angedeutete Skyline am unteren Rand (nur Deko)
  const skyline = [[22, 30], [30, 48], [18, 22], [34, 40], [24, 54], [28, 26], [20, 36]];

  return (
    <button type="button" onClick={() => onNavigate?.('/admin/objekte')} disabled={loading}
      data-tip="Auslastung – Vermietungsquote aller Einheiten, zur Objektübersicht"
      className="dw dw-kpi relative flex w-full flex-col justify-between overflow-hidden border-0 text-left"
      style={{ flex: '1 1 auto', borderRadius: 24, padding: '16px 16px 40px', gap: 12, color: '#fff', fontFamily: 'inherit', cursor: 'pointer',
        background: 'var(--dw-auslastung)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.25), 0 18px 34px -16px var(--dw-auslastung-glow)' }}>
      <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end" style={{ height: 28, gap: 5, padding: '0 16px', opacity: 0.1 }}>
        {skyline.map(([b, h], k) => <span key={k} style={{ width: b, height: h, borderRadius: '4px 4px 0 0', background: '#fff' }} />)}
      </span>
      <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(255,255,255,0.18), transparent 45%)' }} />

      <span className="relative flex items-center" style={{ gap: 10 }}>
        <span className="relative flex shrink-0 items-center justify-center overflow-hidden"
          style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--dw-auslastung)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 0 0 0.5px rgba(0,0,0,0.12), 0 8px 16px -8px rgba(0,0,0,0.45)' }}>
          <Buildings weight="fill" size={18} style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }} />
          <span className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(255,255,255,0.3), transparent 48%)' }} />
        </span>
        <span className="flex flex-col">
          <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em' }}>Auslastung</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>Vermietungsquote</span>
        </span>
      </span>

      {loading ? (
        <span className="relative flex items-center justify-center" style={{ minHeight: 88 }}>
          <CircleNotch size={22} className="animate-spin" style={{ color: 'rgba(255,255,255,0.6)' }} />
        </span>
      ) : (
        <span className="relative flex flex-wrap items-center" style={{ gap: 16 }}>
          <span className="relative shrink-0" style={{ width: 88, height: 88 }}>
            <svg viewBox="0 0 100 100" width={88} height={88} style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="10" />
              <circle cx="50" cy="50" r={radius} fill="none" stroke={ringFarbe} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${filled} ${circumference}`} />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em' }}>{pct}%</span>
          </span>
          <span className="flex flex-1 flex-col" style={{ minWidth: 110, gap: 6, fontSize: 12.5 }}>
            <span className="flex items-center" style={{ gap: 7 }}>
              <House weight="fill" size={14} style={{ color: 'var(--dw-auf-dunkel-ok)' }} />
              <span className="flex-1" style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 600 }}>Vermietet</span>
              <strong className="tabular-nums">{vermietet}</strong>
            </span>
            <span className="flex items-center" style={{ gap: 7 }}>
              <Warning weight="fill" size={14} style={{ color: leerFarbe ?? 'rgba(255,255,255,0.6)' }} />
              <span className="flex-1" style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 600 }}>Leerstand</span>
              <strong className="tabular-nums" style={{ color: leerFarbe }}>{leerstand}</strong>
            </span>
            <span style={{ height: 1, background: 'rgba(255,255,255,0.16)' }} />
            <span className="flex items-center" style={{ gap: 7 }}>
              <Buildings weight="fill" size={14} style={{ color: 'rgba(255,255,255,0.6)' }} />
              <span className="flex-1" style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 600 }}>Gesamt</span>
              <strong className="tabular-nums">{total}</strong>
            </span>
          </span>
        </span>
      )}
    </button>
  );
};

export default LeerstandWidget;
